// KidsTodaySection — surfaces kids' chores for today inside the adult
// Chores view. Pending-approval rows include inline Approve/Deny so
// parents don't need to scroll back to the (collapsed-by-default)
// banner just to act on a single completion.

import { useEffect, useMemo, useState } from "preact/hooks";
import { api, type FamilyStarsMember, type PendingApproval, type UnifiedChore } from "../../lib/rpc";
import "./kids-today.css";

interface Props {
  /** Bumped by the parent on chore mutations so this resyncs. */
  refreshToken: number;
  /** Pending approvals from the parent — used to resolve a row's
   *  completionId so inline Approve/Deny can fire without an extra
   *  lookup. */
  pending: PendingApproval[];
  onApprove: (a: PendingApproval) => void;
  onDeny: (a: PendingApproval, reason: string | null) => void;
}

interface KidGroup {
  kid: FamilyStarsMember;
  chores: UnifiedChore[];
}

export function KidsTodaySection({ refreshToken, pending, onApprove, onDeny }: Props) {
  const [chores, setChores] = useState<UnifiedChore[] | null>(null);
  const [members, setMembers] = useState<FamilyStarsMember[] | null>(null);
  // Which row (if any) is in "writing a deny reason" state. Matches
  // ApprovalsBanner's inline-expansion pattern — no modal, fewer taps.
  const [denyingId, setDenyingId] = useState<number | null>(null);
  const [reasonDraft, setReasonDraft] = useState("");

  useEffect(() => {
    let cancelled = false;
    void Promise.all([
      api.choresAllForHousehold().catch(() => ({ chores: [], today: "" })),
      api.householdsStarsByMember().catch(() => []),
    ]).then(([cr, m]) => {
      if (cancelled) return;
      setChores(cr.chores || []);
      setMembers(m);
    });
    return () => { cancelled = true; };
  }, [refreshToken]);

  const groups: KidGroup[] = useMemo(() => {
    if (!chores || !members) return [];
    const kids = members.filter((m) => m.isKid);
    return kids
      .map((kid) => ({
        kid,
        chores: chores.filter((c) => {
          if (c.kind !== "kid") return false;
          // Parallel chores show in every assignee's section unless
          // that kid has already completed it today (still show if
          // their completion is awaiting approval).
          if (c.assignmentMode === "parallel") {
            if (!c.assignees.includes(kid.userId)) return false;
            if (c.completedTodayBy.includes(kid.userId) && !c.pendingApproval) return false;
            return true;
          }
          // Single-assignee chores still key on `assignee`.
          return c.assignee === kid.userId;
        }),
      }))
      .filter((g) => g.chores.length > 0);
  }, [chores, members]);

  if (chores === null || members === null) return null;
  if (groups.length === 0) return null;

  return (
    <section className="kids-today-section" data-testid="kids-today-section">
      <h2 className="kids-today-head">Kids' chores</h2>
      <div className="kids-today-cols">
        {groups.map((g) => (
          <div key={g.kid.userId} className="kids-today-col">
            <header className="kids-today-col-head">
              <span
                className="kids-today-avatar"
                style={{ background: g.kid.avatarColor || "var(--accent)" }}
                aria-hidden
              >
                {g.kid.avatarEmoji || g.kid.displayName.charAt(0).toUpperCase()}
              </span>
              <div className="kids-today-col-name">{g.kid.displayName}</div>
              <span className="kids-today-stars" data-testid={`kids-today-stars-${g.kid.userId}`}>
                <span aria-hidden>⭐</span> {g.kid.totalStars}
              </span>
            </header>
            <ul className="kids-today-list">
              {g.chores.map((c) => {
                const state = statusOf(c);
                // For pending rows, resolve the matching approval by
                // (choreId === rawId) AND (completedBy === kid.userId)
                // so parallel-mode siblings get their own action set.
                const approval = state === "pending"
                  ? pending.find((p) => p.choreId === c.rawId && p.completedBy === g.kid.userId)
                  : null;
                const isDenying = approval && denyingId === approval.completionId;
                return (
                  <li
                    key={c.id}
                    className="kids-today-row"
                    data-state={state}
                    data-testid={`kids-today-chore-${c.rawId}`}
                  >
                    <span className="kids-today-icon" aria-hidden>{c.icon || "✓"}</span>
                    <span className="kids-today-row-name">{c.name}</span>
                    {approval && !isDenying && (
                      <div className="kids-today-actions">
                        <button
                          type="button"
                          className="kids-today-approve"
                          onClick={() => onApprove(approval)}
                          data-testid={`kids-today-approve-${c.rawId}`}
                        >
                          ✓ Approve
                        </button>
                        <button
                          type="button"
                          className="kids-today-deny"
                          onClick={() => {
                            setDenyingId(approval.completionId);
                            setReasonDraft("");
                          }}
                          data-testid={`kids-today-deny-${c.rawId}`}
                        >
                          Deny
                        </button>
                      </div>
                    )}
                    {!approval && (
                      <span className="kids-today-row-meta">{statusLabel(c)}</span>
                    )}
                    {approval && isDenying && (
                      <div className="kids-today-deny-form">
                        <input
                          type="text"
                          className="kids-today-deny-reason"
                          placeholder="Why? (optional)"
                          value={reasonDraft}
                          maxLength={200}
                          onInput={(e) => setReasonDraft((e.target as HTMLInputElement).value)}
                          autoFocus
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              onDeny(approval, reasonDraft.trim() || null);
                              setDenyingId(null);
                              setReasonDraft("");
                            } else if (e.key === "Escape") {
                              setDenyingId(null);
                              setReasonDraft("");
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="kids-today-deny-confirm"
                          onClick={() => {
                            onDeny(approval, reasonDraft.trim() || null);
                            setDenyingId(null);
                            setReasonDraft("");
                          }}
                        >
                          Deny
                        </button>
                        <button
                          type="button"
                          className="kids-today-deny-cancel"
                          onClick={() => {
                            setDenyingId(null);
                            setReasonDraft("");
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

function statusOf(c: UnifiedChore): "done" | "pending" | "todo" {
  if (c.pendingApproval) return "pending";
  if (c.doneToday) return "done";
  return "todo";
}

function statusLabel(c: UnifiedChore): string {
  if (c.pendingApproval) return "Awaiting review";
  if (c.doneToday) return "Done";
  return "To do";
}
